import { equals, isNil, pick, pluck } from 'ramda';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { parseQuery, stringifyQuery } from '../utils/query';

export const STRING_DESCRIPTOR = {
  encode(value) {
    return value;
  },
  decode(value) {
    return value;
  },
};

const decodeQuery = (values, descriptors) =>
  descriptors.reduce((acc, descriptor) => {
    const value = values[descriptor.name];
    const decoder = descriptor.decode || STRING_DESCRIPTOR.decode;

    acc[descriptor.name] = decoder(value);

    return acc;
  }, {});

const encodeQuery = (values, descriptors) =>
  descriptors.reduce((acc, descriptor) => {
    const value = values[descriptor.name];
    const encoder = descriptor.encode || STRING_DESCRIPTOR.encode;

    if (isNil(value)) {
      return acc;
    }

    acc[descriptor.name] = encoder(value);

    return acc;
  }, {});

/**
 *
 * @param {Array<{ name: String, descriptor: { encode: Function, decode: Function} }>} descriptors should be memoized array
 * @returns {[Object, Function]}
 */
const useQueryState = (descriptors) => {
  const navigate = useNavigate();
  const location = useLocation();

  const initialState = useMemo(
    () => decodeQuery(parseQuery(location.search), descriptors),
    [location.search, descriptors]
  );
  const prevState = useRef(initialState);

  const [state, setState] = useState(initialState);

  useEffect(() => {
    const propertiesNames = pluck('name', descriptors);

    const handleLocationChange = () => {
      const stateAfter = pick(propertiesNames, parseQuery(location.search));
      const stateBefore = encodeQuery(prevState.current, descriptors);
      if (!equals(stateBefore, stateAfter)) {
        const decodedState = decodeQuery(stateAfter, descriptors);
        setState(decodedState); // force re-render
        prevState.current = decodedState;
      }
    };

    handleLocationChange(); // Handle initial render
  }, [location.search, descriptors]);

  const updater = useCallback(
    (values, { type = 'replace', extend = true } = {}) => {
      const newState = extend
        ? {
            ...parseQuery(location.search),
            ...encodeQuery(values, descriptors),
          }
        : encodeQuery(values, descriptors);

      navigate(
        {
          search: stringifyQuery(newState),
        },
        { replace: type === 'replace' }
      );
    },
    [descriptors, location.search, navigate]
  );

  return [state, updater];
};

export default useQueryState;
